// <tento-onboarding-flow>: the view over a FlowController. It owns the frame
// (card, background, nav row), mounts each active step's element as a light-DOM
// child in model order, and syncs the URL hash. Every navigation rule lives in
// the controller (src/controller.ts); nothing here decides what comes next.

import "@tento-ui/components/round";

import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, type Style } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { Round } from "@tento-ui/components/round";
import { Scrollbars } from "@tento-ui/components/scrollbars";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import {
  type ActiveStep,
  FlowController,
  type FlowControllerOptions,
  type FlowState,
} from "../src/controller";
import type { FlowContext } from "../src/gating";

@component({ name: "tento-onboarding-flow" })
export class OnboardingFlow extends LonaWebComponent {
  private $bg = this.hydrate<Round>("bg").bind({
    ...Round.Props.MODAL,
    shadow: "0 1px 4px rgba(0,0,0,0.4)",
  });
  private $prev: HTMLElement = this.$("prev");
  private $next: HTMLElement = this.$("next");

  controller: Option<FlowController<any, HTMLElement>> = null;
  private syncHash = true;
  private unsubscribe: Option<() => void> = null;

  constructor() {
    super();
    GESTURE_MANAGER.addPointerEvent(this.$prev, {
      onClick: () => void this.controller?.prev(),
    });
    GESTURE_MANAGER.addPointerEvent(this.$next, {
      onClick: () => void this.controller?.next(),
    });
  }

  /** Build the controller for a flow and activate it for `context`. The
   * requested initial step is the URL hash unless `requested` is given. */
  async start<App>(
    options: FlowControllerOptions<App, HTMLElement> & {
      context: FlowContext;
      syncHash?: boolean;
      requested?: Option<string>;
    },
  ): Promise<FlowController<App, HTMLElement>> {
    this.unsubscribe?.();
    const controller = new FlowController<App, HTMLElement>(options);
    this.controller = controller;
    this.syncHash = options.syncHash ?? true;
    const offState = controller.subscribe((state) => this.render(state));
    const offEnter = controller.onEnter((step) => this.didEnterStep(step));
    this.unsubscribe = () => {
      offState();
      offEnter();
    };
    await controller.setContext(
      options.context,
      options.requested ?? this.hashId(),
    );
    return controller;
  }

  /** Re-resolve for a new context (e.g. a resize across the breakpoint). */
  async setContext(context: FlowContext): Promise<void> {
    await this.controller?.setContext(context, this.hashId());
  }

  private render(state: FlowState<HTMLElement>) {
    // Mount every active step, in model order, as a light-DOM child.
    let anchor: Option<Element> = null;
    for (const { node, step } of state.steps) {
      const $e = step.element;
      if ($e.id !== node.id) $e.id = node.id;
      const expected: Option<Element> = anchor ? anchor.nextElementSibling : this.firstElementChild;
      if ($e.parentElement !== this || expected !== $e) {
        if (anchor) anchor.after($e);
        else this.prepend($e);
      }
      anchor = $e;
    }
    for (const $child of Array.from(this.children)) {
      const isCurrent = $child === state.current?.step.element;
      if (isCurrent) $child.setAttribute("data-current", "true");
      else $child.removeAttribute("data-current");
    }

    const standalone = state.current?.presentation === "standalone";
    this.toggleAttribute("standalone", standalone);
    this.$prev.textContent = state.labels.back;
    this.$prev.style.display = state.backVisible ? "" : "none";
    this.$next.textContent = state.nextLabel;
    this.$next.toggleAttribute("disabled", !state.nextEnabled);
  }

  private didEnterStep(step: ActiveStep<HTMLElement>) {
    if (!this.syncHash || step.presentation !== "step") return;
    window.location.hash = step.node.id;
  }

  private hashId(): Option<string> {
    const hash = window.location.hash.slice(1);
    return hash.length > 0 ? hash : null;
  }

  private onHashChange = () => {
    const controller = this.controller;
    if (controller == null || !this.syncHash) return;
    if (controller.state().current?.presentation !== "step") return;
    const id = this.hashId();
    const first = controller.state().steps[0]?.node.id;
    const target = id != null && controller.has(id) ? id : first;
    if (target != null) void controller.goTo(target);
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("hashchange", this.onHashChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("hashchange", this.onHashChange);
  }

  static $styles: Style.Compat[] = [
    Scrollbars.$hideScrollbars,
    Button.$style,
    css`
      :host {
        display: flex;
        flex-direction: column;
      }

      #root {
        align-items: center;
        padding-top: var(--onboarding-flow-pad-y, 80px);
        padding-bottom: var(--onboarding-flow-pad-y, 80px);
        margin-inline: var(--onboarding-flow-margin-x, 20px);
        flex-grow: 1;
      }

      #card {
        flex-grow: 1;
        max-width: var(--onboarding-flow-max-width, 1200px);
        width: 100%;
        position: relative;
        min-height: var(--onboarding-flow-min-height, 640px);
      }

      #content {
        position: relative;
        flex-grow: 1;
      }

      #stage {
        display: block;
        position: relative;
        flex-grow: 1;
      }

      ::slotted(*) {
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0px;
        left: 0px;
        display: none;
      }

      ::slotted([data-current="true"]) {
        display: block;
      }

      #nav {
        border-top: 1px solid var(--divider);
        padding: 8px;
      }

      #next {
        min-width: 92px;
      }

      /* A standalone step is shown alone in a compact frame: no nav row, and
         the frame is the card, so a page card inside drops its own chrome. */
      :host([standalone]) #card {
        max-width: var(--onboarding-flow-compact-max-width, 560px);
        min-height: var(--onboarding-flow-compact-min-height, 360px);
      }

      :host([standalone]) #nav {
        display: none;
      }

      :host([standalone]) #stage {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 18px;
        padding: 56px 32px;
        text-align: center;
      }

      :host([standalone]) ::slotted([data-current="true"]) {
        position: static;
        height: auto;
        width: auto;
        --page-card-bg: transparent;
        --page-card-shadow: none;
        --page-card-padding: 0px;
      }

      @media (max-width: 680px) {
        #root {
          padding-top: 12px;
          padding-bottom: 12px;
          margin-inline: 12px;
        }

        :host([standalone]) #stage {
          padding: 40px 22px;
        }
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id=root>
      <std-col id=card>
        <std-round floating id=bg></std-round>
        <std-col id=content>
          <div id=stage><slot></slot></div>
          <std-row id=nav>
            <button id=prev>Back</button>
            <std-spacer></std-spacer>
            <button id=next primary></button>
          </std-row>
        </std-col>
      </std-col>
    </std-col>
  `;
}
